import { FileQuestion, Home, LogIn } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@bliks-io/ui';
import { Logo } from '@/components/Logo';

/**
 * UnauthenticatedNotFound Component
 *
 * 404 page for unauthenticated/public routes.
 * Standalone fullscreen layout without sidebar or header.
 * Matches the visual style of login and auth pages.
 * Provides navigation options to login or go home.
 *
 * @example
 * Used as defaultNotFoundComponent in router configuration:
 * ```tsx
 * export const router = createRouter({
 *   defaultNotFoundComponent: UnauthenticatedNotFound
 * });
 * ```
 */
export function UnauthenticatedNotFound() {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-screen items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md">
        {/* Logo Header */}
        <div className="flex flex-col items-center mb-8">
          <Logo className="mb-4" />
        </div>

        {/* 404 Card */}
        <Card className="w-full">
          <CardHeader>
            <div className="flex items-center gap-2">
              <FileQuestion className="h-5 w-5 text-muted-foreground" />
              <CardTitle>{t('common.NotFound.title')}</CardTitle>
            </div>
            <CardDescription>{t('common.NotFound.description')}</CardDescription>
          </CardHeader>

          <CardContent>
            <p className="text-sm text-muted-foreground">{t('common.NotFound.suggestion')}</p>
          </CardContent>

          <CardFooter className="flex flex-col gap-2">
            <Link to="/login" className="w-full">
              <Button variant="primary" className="gap-2 w-full">
                <LogIn className="h-4 w-4" />
                {t('common.NotFound.goToLogin')}
              </Button>
            </Link>

            <Link to="/" className="w-full">
              <Button variant="outline" className="gap-2 w-full">
                <Home className="h-4 w-4" />
                {t('common.ErrorBoundary.goHome')}
              </Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
